import type { HyloServices, SessionLifetime } from '@/model';

export type AudioOwner = 'chatPlayback' | 'readAloud' | 'composerCapture' | 'call';
export interface AudioLease { release(): void }
export interface AudioFocusService { acquire(owner: AudioOwner, onInterrupted: () => void): AudioLease | undefined }

export function createAudioFocus(isCurrent: () => boolean = () => true): AudioFocusService & { dispose(): void; callStarted(): void; callEnded(): void } {
  let closed = false;
  let current: { owner: AudioOwner; token: object; interrupt(): void } | undefined;
  let bridge: AudioLease | undefined;
  const interrupt = (old: typeof current) => { try { old?.interrupt(); } catch { /* Hardware owners cannot block interruption of another owner. */ } };
  const acquire: AudioFocusService['acquire'] = (owner, onInterrupted) => {
    if (closed || !isCurrent() || current?.owner === 'call' && owner !== 'call') return undefined;
    const old = current; const token = {};
    current = { owner, token, interrupt: onInterrupted }; interrupt(old);
    if (current?.token !== token) return undefined;
    return { release() { if (current?.token === token) current = undefined; } };
  };
  return {
    acquire,
    callStarted() { if (current?.owner !== 'call') bridge = acquire('call', () => { bridge = undefined; }); },
    callEnded() { bridge?.release(); bridge = undefined; },
    dispose() { closed = true; const old = current; current = undefined; bridge = undefined; interrupt(old); },
  };
}
const brokers = new WeakMap<HyloServices, { lifetime: SessionLifetime | null; service: ReturnType<typeof createAudioFocus> }>();
export function audioFocusFor(services: HyloServices): AudioFocusService {
  const lifetime = services.session.lifetime.get(); const held = brokers.get(services);
  if (held?.lifetime === lifetime) return held.service;
  held?.service.dispose();
  const broker = createAudioFocus(() => lifetime !== null && services.session.lifetime.get() === lifetime);
  const started = () => broker.callStarted(); const ended = () => broker.callEnded();
  globalThis.window?.addEventListener('hylo:call-started', started); globalThis.window?.addEventListener('hylo:call-ended', ended);
  const stop = services.session.lifetime.subscribe(() => {
    if (services.session.lifetime.get() === lifetime) return;
    broker.dispose(); stop();
    globalThis.window?.removeEventListener('hylo:call-started', started); globalThis.window?.removeEventListener('hylo:call-ended', ended);
  });
  brokers.set(services, { lifetime, service: broker }); return broker;
}
